import { useState, useEffect, useRef } from "react";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Card, CardContent, CardHeader } from "../../../../ui/card";
import { supabase } from "../../../../../integrations/supabase/client";
import { Loader2, Send, Trash2 } from "lucide-react";
import { useToast } from "../../../../../hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/select";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { OutletMultiSelect } from "./OutletMultiSelect";

type Message = {
  role: "user" | "assistant";
  content: string;
  timestamp?: string;
};

const SUGGESTED_QUESTIONS = [
  "What are customers complaining about most?",
  "What do customers love about us?",
  "Which outlet has the best service?",
  "Are there any urgent issues?",
  "What should we improve?",
  "Summarize negative reviews about delivery.",
];

interface AIChatProps {
  preselectedOutlets?: string[];
  supabaseoutletIDs: string[];
}

export const AIChat = ({ preselectedOutlets, supabaseoutletIDs }: AIChatProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string>("all");
  const [selectedOutlets, setSelectedOutlets] = useState<string[]>(["all"]);
  const [outlets, setOutlets] = useState<any[]>([]);
  const { toast } = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadChatHistory();
    loadOutlets();
  }, [selectedOutlets, selectedPlatform]);

  useEffect(() => {
    if (preselectedOutlets) {
      setSelectedOutlets(preselectedOutlets);
    }
  }, [preselectedOutlets]);

  const loadOutlets = async () => {
    const { data } = await supabase
      .from("outlets")
      .select("id, outlet_name, location").in('id', supabaseoutletIDs)
      .order("outlet_name");
    if (data) setOutlets(data);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadChatHistory = async () => {
    try {
      let query = supabase
        .from("chat_messages")
        .select("*")
        .order("created_at", { ascending: true })
        .limit(50);

      // Filter by selected outlets if specific outlets are chosen
      if (selectedOutlets.length > 0 && !selectedOutlets.includes("all")) {
        query = query.contains("outlet_ids", selectedOutlets);
      }else {
        query = query.contains("outlet_ids", supabaseoutletIDs);
      }

      // Filter by platform if selected
      if (selectedPlatform !== "all") {
        query = query.eq("platform", selectedPlatform);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (data && data.length > 0) {
        const chatMessages = data.map((msg) => ({
          role: msg.role as "user" | "assistant",
          content: msg.message_text,
          timestamp: new Date(msg.created_at!).toLocaleTimeString(),
        }));
        setMessages(chatMessages);
      }
    } catch (error) {
      console.error("Error loading chat history:", error);
    }
  };

  const handleSend = async (question?: string) => {
    const messageText = question || input.trim();
    if (!messageText) return;

    setLoading(true);
    const userMsg: Message = {
      role: "user",
      content: messageText,
      timestamp: new Date().toLocaleTimeString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");

    try {
      const { data, error } = await supabase.functions.invoke("chat-with-reviews", {
        body: {
          message: messageText,
          filters: {
            outlet_ids: (!selectedOutlets || selectedOutlets.length === 0 || selectedOutlets.includes("all")) 
              ? supabaseoutletIDs 
              : selectedOutlets,
            platform: selectedPlatform !== "all" ? selectedPlatform : null,
          },
        },
      });

      if (error) throw error;

      const assistantMsg: Message = {
        role: "assistant",
        content: data.message,
        timestamp: new Date().toLocaleTimeString(),
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (error: any) {
      console.error("Chat error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to get AI response",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClearChat = async () => {
    try {
      const { error } = await supabase.from("chat_messages").delete().neq("id", "00000000-0000-0000-0000-000000000000");

      if (error) throw error;

      setMessages([]);
      toast({
        title: "Chat cleared",
        description: "All messages have been removed",
      });
    } catch (error) {
      console.error("Error clearing chat:", error);
      toast({
        title: "Error",
        description: "Failed to clear chat history",
        variant: "destructive",
      });
    }
  };

  const InitialMessage = () => (
    <div className="flex gap-3 mb-6">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
        💬
      </div>
      <div className="flex-1">
        <div className="bg-muted rounded-lg p-4 mb-2">
          <p className="text-sm leading-relaxed mb-3">
            Hello! I'm ready to help you analyze your customer reviews. Since I don't have any review data available yet, I can't provide specific insights or examples. However, once you share the review data from platforms like Google, TripAdvisor, Facebook, Yelp, Grab, or Foodpanda, I can:
          </p>
          <ol className="text-sm space-y-1 ml-4 list-decimal">
            <li>Identify common themes (e.g., service quality, food taste, pricing)</li>
            <li>Highlight positive and negative feedback patterns</li>
            <li>Provide actionable recommendations based on the feedback</li>
            <li>Compare performance across different platforms</li>
            <li>Help you understand customer satisfaction trends</li>
          </ol>
          <p className="text-sm mt-3">
            Please share your review data, and I'll be happy to assist you further!
          </p>
        </div>
        <span className="text-xs text-muted-foreground">{new Date().toLocaleTimeString()}</span>
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold">EpicReview Intelligence</h2>
          <p className="text-sm text-muted-foreground">Multi-Platform Review Analytics & AI Insights</p>
        </div>
        {messages.length > 0 && (
          <Button variant="outline" size="sm" onClick={handleClearChat}>
            <Trash2 className="mr-2 h-4 w-4" />
            Clear Chat
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 flex-wrap">
            <OutletMultiSelect
              outlets={outlets}
              selectedOutlets={selectedOutlets}
              onChange={setSelectedOutlets}
            />
            <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Platforms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Platforms</SelectItem>
                <SelectItem value="google_maps">Google Maps</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="tiktok">TikTok</SelectItem>
                <SelectItem value="facebook">Facebook</SelectItem>
                <SelectItem value="tripadvisor">TripAdvisor</SelectItem>
                <SelectItem value="epicreview">EpicReview</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Chat Card */}
      <Card className="shadow-lg">
        <CardHeader className="border-b">
          <h3 className="text-lg font-semibold">🧠 AI Review Assistant</h3>
        </CardHeader>
        <CardContent className="p-6">
          {/* Messages */}
          <div className="min-h-[400px] max-h-[500px] overflow-y-auto mb-6 space-y-6 px-2">
            {messages.length === 0 && <InitialMessage />}

            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-3 ${msg.role === "user" ? "justify-end" : ""}`}>
                {msg.role === "assistant" && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    💬
                  </div>
                )}
                <div className={`${msg.role === "user" ? "max-w-[85%]" : "flex-1 max-w-[85%]"}`}>
                  <div
                    className={`rounded-2xl px-5 py-3 ${
                      msg.role === "user"
                        ? "bg-primary/90 text-primary-foreground shadow-md"
                        : "bg-muted"
                    }`}
                  >
                    <div className="text-sm prose prose-sm dark:prose-invert max-w-none">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                          ul: ({ children }) => <ul className="list-disc ml-4 mb-2 space-y-1">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal ml-4 mb-2 space-y-1">{children}</ol>,
                          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                          em: ({ children }) => <em className="italic">{children}</em>,
                          code: ({ inline, children }: any) =>
                            inline ? (
                              <code className="bg-background/50 px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>
                            ) : (
                              <code className="block bg-background/50 p-3 rounded-md my-2 text-xs font-mono overflow-x-auto">{children}</code>
                            ),
                          pre: ({ children }) => <pre className="my-2">{children}</pre>,
                          a: ({ href, children }) => (
                            <a href={href} className="text-primary underline hover:no-underline" target="_blank" rel="noopener noreferrer">
                              {children}
                            </a>
                          ),
                          h1: ({ children }) => <h1 className="text-lg font-bold mb-2 mt-3 first:mt-0">{children}</h1>,
                          h2: ({ children }) => <h2 className="text-base font-bold mb-2 mt-3 first:mt-0">{children}</h2>,
                          h3: ({ children }) => <h3 className="text-sm font-bold mb-2 mt-2 first:mt-0">{children}</h3>,
                          blockquote: ({ children }) => (
                            <blockquote className="border-l-4 border-primary/30 pl-4 italic my-2">{children}</blockquote>
                          ),
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground mt-1 block">
                    {msg.timestamp}
                  </span>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  💬
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Analyzing your reviews…</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Suggested Questions (only show when no messages) */}
          {messages.length === 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium mb-3">💡 Suggested Questions</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {SUGGESTED_QUESTIONS.map((question, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    className="justify-start text-left h-auto py-3 px-4"
                    onClick={() => handleSend(question)}
                    disabled={loading}
                  >
                    {question}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Input Bar */}
          <div className="flex gap-3 items-end">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && !loading && handleSend()}
              placeholder="Ask about your reviews..."
              disabled={loading}
              className="flex-1 h-12 px-4 text-base bg-background border-2 focus-visible:ring-2"
            />
            <Button 
              onClick={() => handleSend()} 
              disabled={loading || !input.trim()}
              className="h-12 px-6"
              size="lg"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
